/* eslint-disable @typescript-eslint/naming-convention */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { StateService } from '@app/shared/state.service';
import { ClientResources } from '@app/client-resources.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '@app/shared/utils.service';
import { Idle } from '@ng-idle/core';
import { ConstantsService } from '@app/shared/constants.service';

const applicationJson = 'application/json';
const applicationFormUrlEncoded = 'application/x-www-form-urlencoded';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  urlForApi: string;
  authUrl: string;

  constructor(
    private http: HttpClient,
    private stateService: StateService,
    private clientResources: ClientResources,
    private constantsService: ConstantsService,
    private translateService: TranslateService,
    private idle: Idle
  ) {}

  setHeaders(auth: string, contentType?: string) {
    let headers = new HttpHeaders();
    if (auth === 'Bearer') {
      this.idle.watch(); // reset timout counter on seucred api call;
      headers = headers.set('Authorization', 'Bearer ' + this.stateService.getAccessToken());
    } else if (auth === '') {
    } else {
      headers = headers.set('recaptcha', auth);
    }
    headers = headers.set('Content-Type', contentType);
    headers = headers.set('Accept-Language', this.translateService.currentLang);
    headers = headers.set('Cache-Control', 'no-store, no-cache, must-revalidate');
    headers = headers.set('Pragma', 'no-cache');
    headers = headers.set('Expires', '0');
    headers = headers.set('If-Modified-Since', '0');
    if (this.stateService.getClientId() === 'bcrgeorgeclient') {
      headers = headers.set('webapiclient', 'GEORGE');
    }
    if (this.stateService.getClientId() === 'georgegoclientro') {
      const os = UtilsService.browserOsVersion();
      if (os.os === 'Android') {
        headers = headers.set('webapiclient', 'GEORGEGO Android');
      }
      if (os.os === 'iOS') {
        headers = headers.set('webapiclient', 'GEORGEGO iOS');
      }
    }
    return headers;
  }

  forceChangePassword(data: any) {
    this.setUrlForApi(this.clientResources.getAuthUrl() + ConstantsService.urlContext.ForceChangePassword);

    return this.http.put(this.getUrlForApi(), data, {
      headers: this.setHeaders('', applicationJson),
    });
  }

  resetPassword(data: any, recaptcha: string) {
    let body = new HttpParams();
    body = body.append('username', data.userName);
    body = body.append('appcode', this.constantsService.getAppCode());
    this.setUrlForApi(this.prepEndpoint(ConstantsService.urlContext.ResetPassword));

    return this.http.post(this.getUrlForApi(), body, {
      headers: this.setHeaders(recaptcha, applicationFormUrlEncoded),
    });
  }

  sendActivationCode(data: any, recaptcha: string) {
    let body = new HttpParams();
    body = body.append('appcode', this.constantsService.getAppCode());
    body = body.append('username', data.userName);
    body = body.append('tokenserial', data.token);
    this.setUrlForApi(this.prepEndpoint(ConstantsService.urlContext.SendActivationCode));

    return this.http.post(this.getUrlForApi(), body, {
      headers: this.setHeaders(recaptcha, applicationFormUrlEncoded),
    });
  }

  sendRecoverUsernameCode(data: any, recaptcha: string, localCustomer: boolean) {
    const body = new URLSearchParams();

    if (localCustomer) {
      body.set('cnp', data.cnpInput);
      body.set('phoneNumber', data.phoneNumberInput);
    } else {
      body.set('cnp', data.cnpInput2);
      body.set('phoneNumber', data.phoneNumberInput2);
    }

    if (data.cuiInput) {
      body.set('cui', data.cuiInput);
    }

    if (data.entityType) {
      body.set('entityType', data.entityType);
    }

    this.setUrlForApi(this.prepEndpoint(ConstantsService.urlContext.RecoverUsername));

    return this.http.post(this.getUrlForApi(), body.toString(), {
      headers: this.setHeaders(recaptcha, applicationFormUrlEncoded),
    });
  }

  prepEndpoint(ep: string) {
    return this.clientResources.getBaseUrl() + ep;
  }

  // Get QrCode
  getQrCode(username: string) {
    this.setUrlForApi(
      this.clientResources.getAuthUrl() + ConstantsService.urlContext.GetQrCode + '?username=' + username
    );

    return this.http.get(this.getUrlForApi(), {
      headers: this.setHeaders('Bearer', applicationJson),
      withCredentials: true,
    });
  }

  // Post login operation
  postLoginOperation(username: string) {
    this.setUrlForApi(this.clientResources.getAuthUrl() + ConstantsService.urlContext.LoginOperation);

    return this.http.post(
      this.getUrlForApi(),
      { username },
      {
        headers: this.setHeaders('Bearer', applicationJson),
        withCredentials: true,
      }
    );
  }

  // Post select entity
  postSelectEntity(selectedEntity: string, signId: any) {
    this.setUrlForApi(this.clientResources.getAuthUrl() + ConstantsService.urlContext.selectEntity + signId);

    return this.http.post(this.getUrlForApi(), JSON.stringify(selectedEntity), {
      headers: this.setHeaders('Bearer', applicationJson),
      withCredentials: true,
    });
  }

  getPollStatus(url: string) {
    return this.http.get(url, {
      headers: this.setHeaders('Bearer', 'application/json'),
      withCredentials: true,
      observe: 'response',
    });
  }

  // Get pooling qrcode
  getPoolingQrCode(url: string) {
    return this.http.get(url, {
      headers: this.setHeaders('Bearer', applicationJson),
      withCredentials: true,
    });
  }

  // PUT QrCode verification
  putQrCodeVerification(signId: any, data: any) {
    this.setUrlForApi(this.clientResources.getAuthUrl() + ConstantsService.urlContext.webSocketVerify + signId);

    return this.http.put(this.getUrlForApi(), JSON.stringify(data), {
      headers: this.setHeaders('Bearer', applicationJson),
      withCredentials: true,
    });
  }

  postAuthOptions(data: any) {
    this.setUrlForApi(this.clientResources.getBaseUrl() + ConstantsService.urlContext.UserOptions);

    return this.http.get(this.getUrlForApi(), {
      // headers: this.setHeaders('Bearer', applicationJson),
      withCredentials: true,
      params: data,
    });
  }

  getUrlForApi() {
    return this.urlForApi;
  }

  setUrlForApi(value: any) {
    this.urlForApi = value;
  }
}
